/**
 * SPA hooks for Keepa query schedules — Pass 5 wiring.
 *
 * Surfaces:
 *   - useKeepaSchedules()            → /schedules list
 *   - useCreateKeepaSchedule()       → schedule-create modal
 *   - useUpdateKeepaSchedule(id)     → pause / resume / edit
 *   - useReconfirmKeepaSchedule()    → 30-day reconfirm button
 *   - useDeleteKeepaSchedule()       → row delete
 *   - usePreviewCron()               → live "next fire" preview in the modal
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateKeepaScheduleRequest,
  KeepaScheduleResponse,
  ListKeepaSchedulesResponse,
  PreviewCronResponse,
  RunScheduleNowResponse,
  UpdateKeepaScheduleRequest,
} from '@sourcito/schemas';
import { apiFetch } from './api';

export const keepaSchedulesQuery = queryOptions({
  queryKey: ['keepa-schedules'] as const,
  queryFn: () =>
    apiFetch<ListKeepaSchedulesResponse>('/api/keepa-schedules', {
      responseSchema: ListKeepaSchedulesResponse,
    }),
  staleTime: 30_000,
});

export function useKeepaSchedules() {
  return useQuery(keepaSchedulesQuery);
}

export function useCreateKeepaSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateKeepaScheduleRequest) =>
      apiFetch<KeepaScheduleResponse>('/api/keepa-schedules', {
        method: 'POST',
        body: JSON.stringify(input),
        responseSchema: KeepaScheduleResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keepa-schedules'] });
    },
  });
}

export function useUpdateKeepaSchedule(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateKeepaScheduleRequest) =>
      apiFetch<KeepaScheduleResponse>(`/api/keepa-schedules/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        responseSchema: KeepaScheduleResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keepa-schedules'] });
    },
  });
}

export function useReconfirmKeepaSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<KeepaScheduleResponse>(`/api/keepa-schedules/${id}/reconfirm`, {
        method: 'POST',
        responseSchema: KeepaScheduleResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keepa-schedules'] });
    },
  });
}

export function useDeleteKeepaSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<null>(`/api/keepa-schedules/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keepa-schedules'] });
    },
  });
}

export function usePreviewCron() {
  return useMutation({
    mutationFn: (cronExpression: string) =>
      apiFetch<PreviewCronResponse>('/api/keepa-schedules/preview-cron', {
        method: 'POST',
        body: JSON.stringify({ cronExpression }),
        responseSchema: PreviewCronResponse,
      }),
  });
}

/**
 * On-demand "Run now" — queues an immediate keepa_search_runs row using the
 * schedule's bound template + parameter values. Bypasses the cap / reconfirm
 * / paused-state gates by design; only the template + fee-profile gates can
 * surface as errors. The actual run kicks off on the next cron-sweep tick
 * (≤60s).
 */
export function useRunKeepaScheduleNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<RunScheduleNowResponse>(`/api/keepa-schedules/${id}/run`, {
        method: 'POST',
        responseSchema: RunScheduleNowResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keepa-schedules'] });
    },
  });
}
